import React, { useState } from 'react';
import type { IntegrationProvider } from '../../lib/integration-types';

const PROVIDER_INFO: Record<IntegrationProvider, { name: string; icon: string; description: string; scopes: string[] }> = {
  slack: { name: 'Slack', icon: '💬', description: 'Send messages and receive events', scopes: ['chat:write', 'channels:read', 'groups:read'] },
  notion: { name: 'Notion', icon: '📝', description: 'Create and update pages', scopes: ['read', 'write'] },
  github: { name: 'GitHub', icon: '🐙', description: 'Create issues and PRs', scopes: ['repo', 'read:user'] },
  hubspot: { name: 'HubSpot', icon: '🔶', description: 'CRM operations', scopes: ['crm.objects.contacts.read', 'crm.objects.contacts.write'] },
  google_workspace: { name: 'Google Workspace', icon: '📁', description: 'Drive, Calendar, Gmail', scopes: ['drive', 'calendar'] },
};

interface Props {
  provider: IntegrationProvider;
  onConnect: (name: string) => void;
  onCancel: () => void;
}

export function IntegrationConnectModal({ provider, onConnect, onCancel }: Props) {
  const [name, setName] = useState('');
  const info = PROVIDER_INFO[provider];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onConnect(name.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{info.icon}</span>
            <h2 className="text-lg font-semibold">Connect {info.name}</h2>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <p className="text-gray-600 mb-4">{info.description}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Connection Name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={`My ${info.name} Connection`}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Permissions requested:</p>
            <ul className="text-xs text-gray-600 space-y-1">
              {info.scopes.map(scope => (
                <li key={scope}>• {scope}</li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Authorize
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
